import React from 'react';
import { Button, Box, Stack, Typography, Checkbox } from '@mui/material';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { useMutation } from 'react-fetching-library';
import { DebounceInput } from 'react-debounce-input';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

import { SmallFormTextField } from '../../../../../components/small-fields/SmallFormTextField';
import { AlertDialogContainer } from '../../../../../components/others/AlertDialogContainer';
import { submitPiggyBankConfigAction } from '../../../../../services/request';
import { ConfirmDialog } from '../../../../../components/ConfirmDialog';
import { SmallFormDateField } from '../../../../../components/small-fields/SmallFormDateField';
import { formatDate, extractApiErrorMessage } from '../../../../../services/functions';

export const PiggyBankConfigForm = props => {
  const { data, submitCallback, handleClose } = props;
  const { loading, mutate } = useMutation(submitPiggyBankConfigAction);
  const [showAlert, setShowAlert] = React.useState(false);
  const [severity, setSeverity] = React.useState('');
  const [title, setTitle] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [confirmDeleteOpen, setConfirmDeleteOpen] = React.useState(false);

  const validationSchema = Yup.object({
    date_from: Yup.date().required('To pole jest wymagane'),
    date_to: Yup.date().required('To pole jest wymagane'),
    rate: Yup.number().required('To pole jest wymagane'),
    entry_valid_days: Yup.number().required('To pole jest wymagane')
      .min(1, 'Wartość musi być większa od 0')
  });

  const initialValues = {
    date_from: data ? data.date_from : null,
    date_to: data ? data.date_to : null,
    rate: data ? data.rate : 0,
    message: data ? data.message : '',
    skip_transport: data ? data.skip_transport : true,
    entry_valid_days: data ? data.entry_valid_days : 730
  };

  const handleSubmit = async values => {
    const { error, payload } = await mutate(values, data ? 'PUT' : 'POST', data ? data.id : undefined);
    if (!error) {
      submitCallback(payload, data ? 'edit' : 'created', 'success', data ? 'Zaktualizowano konfigurację skarbonki' : 'Dodano nową konfigurację skarbonki', '');
      handleClose();
    } else {
      setSeverity('error');
      setTitle('Nie udało się zapisać konfiguracji skarbonki');
      setMessage(extractApiErrorMessage(payload));
      setShowAlert(true);
    }
  };

  const handleDelete = async () => {
    const { error, payload } = await mutate([], 'DELETE', data.id);
    if (!error) {
      submitCallback(data, 'delete', 'success', 'Usunięto konfigurację skarbonki', '');
      handleClose();
    } else {
      setSeverity('error');
      setTitle('Nie udało się usunąć konfiguracji skarbonki');
      setMessage(extractApiErrorMessage(payload));
      setShowAlert(true);
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit: values => {
      handleSubmit(values);
    }
  });

  return (
    <>
      {showAlert && (
        <AlertDialogContainer
          severity={severity}
          title={title}
          message={message}
          setShowAlert={setShowAlert}
        />
      )}
      <form onSubmit={formik.handleSubmit} style={{ textAlign: 'center' }}>
        <Box display="flex" flexDirection="column" mt={1} p={1} ml="5%" mr="5%">
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 25%">Data od</Typography>
            <SmallFormDateField
              sx={{ flex: '1 1 90%' }}
              name="date_from"
              id="date_from"
              type="text"
              placeholder="date_from"
              value={formik.values.date_from}
              onChange={val => formik.setFieldValue('date_from', formatDate(val))}
              error={formik.touched.date_from && Boolean(formik.errors.date_from)}
              helperText={formik.touched.date_from && formik.errors.date_from}
            />
          </Stack>
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 25%">Data do</Typography>
            <SmallFormDateField
              sx={{ flex: '1 1 90%' }}
              name="date_to"
              id="date_to"
              type="text"
              placeholder="date_to"
              value={formik.values.date_to}
              onChange={val => formik.setFieldValue('date_to', formatDate(val))}
              error={formik.touched.date_to && Boolean(formik.errors.date_to)}
              helperText={formik.touched.date_to && formik.errors.date_to}
            />
          </Stack>
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 25%">Mnożnik kwoty doliczanej do skarbonki (%)</Typography>
            <DebounceInput
              sx={{ flex: '1 1 90%' }}
              name="rate"
              id="rate"
              type="text"
              placeholder="np. 5"
              value={formik.values.rate}
              onChange={formik.handleChange}
              error={formik.touched.rate && Boolean(formik.errors.rate)}
              helperText={formik.touched.rate && formik.errors.rate}
              debounceTimeout={300}
              element={SmallFormTextField}
            />
          </Stack>
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 25%">Komunikat na stronę</Typography>
            <DebounceInput
              sx={{ flex: '1 1 90%' }}
              name="message"
              id="message"
              type="text"
              placeholder="komunikat"
              value={formik.values.message}
              onChange={formik.handleChange}
              error={formik.touched.message && Boolean(formik.errors.message)}
              helperText={formik.touched.message && formik.errors.message}
              debounceTimeout={300}
              element={SmallFormTextField}
            />
          </Stack>
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 25%">Ważność punktów (dni)</Typography>
            <DebounceInput
              sx={{ flex: '1 1 90%' }}
              name="entry_valid_days"
              id="entry_valid_days"
              type="number"
              placeholder="np. 730"
              value={formik.values.entry_valid_days}
              onChange={formik.handleChange}
              error={formik.touched.entry_valid_days && Boolean(formik.errors.entry_valid_days)}
              helperText={formik.touched.entry_valid_days && formik.errors.entry_valid_days}
              debounceTimeout={300}
              element={SmallFormTextField}
            />
          </Stack>
          <Stack direction="row" alignItems="center" justifyContent="right" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 30%">Nie wliczaj transportu</Typography>
            <Checkbox
              sx={{ flex: '1 1 70%' }}
              id="skip_transport"
              name="skip_transport"
              checked={formik.values.skip_transport}
              onChange={formik.handleChange} />
          </Stack>
        </Box>
        <Button
          color="primary"
          variant="contained"
          type="submit"
          sx={{ mt: 3 }}
          disabled={loading}
        >
          <AddIcon />
          Zapisz
        </Button>
        { data
          ? <Button
            color="error"
            variant="contained"
            sx={{ mt: 3, ml: 4 }}
            onClick={() => {
              setConfirmDeleteOpen(true);
            }}
          >
            <DeleteIcon />
          Usuń
          </Button>
          : null}
      </form>
      <ConfirmDialog
        title="Usunąć konfigurację skarbonki?"
        open={confirmDeleteOpen}
        setOpen={setConfirmDeleteOpen}
        onConfirm={handleDelete}
        disabled={loading}
      >
        Czy na pewno chcesz usunąć tę konfigurację skarbonki?
      </ConfirmDialog>
    </>

  );
};
